import { normalizeHedgeCrossAssetRiskGroup } from '@src/components/AutoHedging/crossAssetRiskGroup';
import type { HedgeCommandResponse, SingleHedgeCommandPayload } from '@src/components/AutoHedging/types';
import { useGetOpenTransactionsMenuItems } from '@src/containers/Trading/Markets/AccountLedgerEvents/useGetOpenTransactionsMenuItems';
import { useHedgeRuleCommandMutation } from '@src/containers/Trading/Markets/PositionAutoHedgingRules/queries';
import { useEmbeddedContext } from '@src/hooks/useEmbeddedContext';
import { useFeatureFlag } from '@src/hooks/useFeatureFlag';
import { useNavigateToTradingOrderDetails } from '@src/hooks/useNavigateToTradingOrderDetails';
import { useRoleAuth } from '@src/hooks/useRoleAuth';
import type { IHedgePositionStatusRow } from '@src/providers/HedgePositionStatusProvider';
import {
  abbreviateId,
  ACTION,
  getRowNodesToOperateOn,
  HedgeCommandEnum,
  HedgeControlStatusEnum,
  IconName,
  LedgerAccountTypeEnum,
  MixpanelEventSource,
  useDynamicCallback,
  type AgGridMenuItems,
} from '@talos/kyoko';
import type { GetContextMenuItemsParams, IRowNode } from 'ag-grid-community';
import { compact } from 'lodash-es';
import { useCallback } from 'react';
import { from, mergeMap } from 'rxjs';
import { openDetailsMenuItem } from '../Orders/useOrderMenu';

export const useHedgePositionStatusMenu = (): {
  getCustomContextMenuItems: (params: GetContextMenuItemsParams<IHedgePositionStatusRow>) => AgGridMenuItems;
} => {
  const { isAuthorized } = useRoleAuth();
  const { enableHedgeRuleCrossAssetRiskGroup } = useFeatureFlag();
  const { isEmbedded } = useEmbeddedContext();

  const openDetailsItem = useDynamicCallback((rows: IRowNode<IHedgePositionStatusRow>[]) => {
    const orderIds = rows
      .map(row => row.data?.HedgeOrderID)
      .filter<string>((maybeString): maybeString is string => maybeString !== undefined);

    if (orderIds.length === 0) {
      return null;
    }

    return openDetailsMenuItem({
      orders: orderIds.map(OrderID => ({ OrderID })),
      onOpenDetails,
      text: 'Open Hedge Order Details',
      isEmbedded,
    });
  });

  const hedgeRuleCommandMutation = useHedgeRuleCommandMutation();
  const sendHedgeCommand = useCallback(
    (payload: SingleHedgeCommandPayload) => {
      return hedgeRuleCommandMutation.mutateAsync(payload);
    },
    [hedgeRuleCommandMutation]
  );

  const getOpenTransactionsMenuItems = useGetOpenTransactionsMenuItems();
  const onOpenDetails = useNavigateToTradingOrderDetails();

  const getCustomContextMenuItems = useDynamicCallback((params: GetContextMenuItemsParams<IHedgePositionStatusRow>) => {
    const rows = getRowNodesToOperateOn(params);
    const hedgeRule = rows?.[0]?.data;
    const hedgeRuleSubAccountName =
      hedgeRule && params.context.current.subAccountsByID?.get(hedgeRule?.AccountID)?.Name;

    return compact([
      isAuthorized(ACTION.VIEW_ORDER) ? openDetailsItem(rows) : null,
      enableHedgePositionMenuItem({ rows, sendHedgeCommand, enableHedgeRuleCrossAssetRiskGroup }),
      disableHedgePositionMenuItem({ rows, sendHedgeCommand, enableHedgeRuleCrossAssetRiskGroup }),
      ...(isAuthorized(ACTION.VIEW_LEDGER_EVENTS) && hedgeRule && hedgeRuleSubAccountName
        ? getOpenTransactionsMenuItems({
            asset: hedgeRule.Asset,
            account: hedgeRuleSubAccountName,
            accountType: LedgerAccountTypeEnum.SubAccount,
            source: MixpanelEventSource.PositionAutoHedgingBlotter,
            name: `Open transactions for: ${abbreviateId(hedgeRule.HedgeRuleID)}`,
          })
        : []),
    ]);
  });

  return {
    getCustomContextMenuItems,
  };
};

const enableHedgePositionMenuItem = ({
  rows,
  sendHedgeCommand,
  enableHedgeRuleCrossAssetRiskGroup,
}: {
  rows: IRowNode<IHedgePositionStatusRow>[];
  sendHedgeCommand: (payload: SingleHedgeCommandPayload) => Promise<HedgeCommandResponse>;
  enableHedgeRuleCrossAssetRiskGroup: boolean;
}) => {
  const disabledRows = compact(
    rows.filter(row => row.data?.HedgeControlStatus === HedgeControlStatusEnum.Disabled).map(row => row.data)
  );

  if (disabledRows.length === 0) {
    return null;
  }

  return {
    name: `Enable ${disabledRows.length} Hedge Position${disabledRows.length > 1 ? 's' : ''}`,
    action: () => {
      return from(disabledRows)
        .pipe(
          mergeMap(
            status =>
              sendHedgeCommand({
                Command: HedgeCommandEnum.Activate,
                HedgeRuleID: status.HedgeRuleID,
                HedgeControlType: 'Position',
                ...(enableHedgeRuleCrossAssetRiskGroup
                  ? { CrossAssetRiskGroup: normalizeHedgeCrossAssetRiskGroup(status.CrossAssetRiskGroup) }
                  : {}),
              }),
            5 // make sure at most 5 requests are sent at a time
          )
        )
        .subscribe();
    },
    icon: `<i class="ag-icon ${IconName.CheckCircleSolid}"/>`,
  };
};

const disableHedgePositionMenuItem = ({
  rows,
  sendHedgeCommand,
  enableHedgeRuleCrossAssetRiskGroup,
}: {
  rows: IRowNode<IHedgePositionStatusRow>[];
  sendHedgeCommand: (payload: SingleHedgeCommandPayload) => Promise<HedgeCommandResponse>;
  enableHedgeRuleCrossAssetRiskGroup: boolean;
}) => {
  const enabledRows = compact(
    rows
      .filter(
        row =>
          row.data?.HedgeControlStatus !== HedgeControlStatusEnum.Disabled &&
          row.data?.HedgeControlStatus !== HedgeControlStatusEnum.Disabling
      )
      .map(row => row.data)
  );

  if (enabledRows.length === 0) {
    return null;
  }

  return {
    name: `Disable ${enabledRows.length} Hedge Position${enabledRows.length > 1 ? 's' : ''}`,
    action: () => {
      return from(enabledRows)
        .pipe(
          mergeMap(
            status =>
              sendHedgeCommand({
                Command: HedgeCommandEnum.Kill,
                HedgeRuleID: status.HedgeRuleID,
                HedgeControlType: 'Position',
                ...(enableHedgeRuleCrossAssetRiskGroup
                  ? { CrossAssetRiskGroup: normalizeHedgeCrossAssetRiskGroup(status.CrossAssetRiskGroup) }
                  : {}),
              }),
            5 // make sure at most 5 requests are sent at a time
          )
        )
        .subscribe();
    },
    icon: `<i class="ag-icon ${IconName.CheckCircleSolid}"/>`,
  };
};
